import type { CalendarEvent } from "@content-os/types";

/**
 * One thing going out: a post and every platform/format it publishes to at
 * the same moment (e.g. a TikTok plus its Instagram Story, Facebook Story and
 * YouTube Short). Scheduled rows that share a groupId and time are one post.
 */
export interface UpcomingPost {
  key: string;
  scheduledFor: string;
  /** The row whose caption and media represent the post. */
  lead: CalendarEvent;
  events: CalendarEvent[];
  /** True when any row in the group failed to publish. */
  hasFailure: boolean;
}

export function groupUpcomingPosts(events: readonly CalendarEvent[]): UpcomingPost[] {
  const groups = new Map<string, CalendarEvent[]>();
  for (const event of events) {
    const key = `${event.groupId ?? event.id}|${event.scheduledFor}`;
    const list = groups.get(key);
    if (list) list.push(event);
    else groups.set(key, [event]);
  }
  return [...groups.entries()]
    .map(([key, list]) => {
      // Prefer the original post over its auto-added Stories/Shorts as the lead.
      const lead = list.find((e) => !isCompanion(e)) ?? list[0];
      return {
        key,
        scheduledFor: lead.scheduledFor,
        lead,
        events: list,
        hasFailure: list.some((e) => e.status === "FAILED"),
      };
    })
    .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
}

function isCompanion(event: CalendarEvent): boolean {
  return (event.metadata as Record<string, unknown> | undefined)?.videoCompanion === true;
}

/** Local calendar-day key (YYYY-MM-DD) for grouping by day. */
export function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Whole local days from `now` to `date` (0 = today, 1 = tomorrow, -1 = yesterday). */
export function daysFromToday(date: Date, now: Date = new Date()): number {
  const a = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const b = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86_400_000);
}

/** "5 min ago", "3 h ago", "2 days ago" in the user's language. */
export function formatRelativeTime(date: Date, locale: string, now: Date = new Date()): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" });
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  if (abs < 60) return rtf.format(0, "minute");
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), "hour");
  if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), "day");
  return date.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

/** First non-empty line of a caption, hashtags removed, for one-line previews. */
export function captionPreview(caption: string | null | undefined): string {
  const line = (caption ?? "")
    .split("\n")
    .map((l) => l.replace(/#[\p{L}\p{N}_]+/gu, "").trim())
    .find((l) => l.length > 0);
  return line ?? "";
}
